import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Send, ClipboardCopy, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PanoQuestion from "@/components/pano/PanoQuestion";
import WilsonUploads from "@/components/pano/WilsonUploads";
import CoordWarning, { coordCheck, LAT_Q } from "@/components/pano/CoordWarning";
import SendConfirmDialog from "@/components/pano/SendConfirmDialog";

const WILSON_Q = "1288245380";
import { missingRequired, skippedSections, summaryText } from "@/lib/panoAudit";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");

const StatusBar = ({ missing, sentAt, sentBy }) => (
  <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="pano-audit-status">
    {missing.length === 0 ? (
      <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 className="w-4 h-4" /> All required questions answered</span>
    ) : (
      <span className="inline-flex items-center gap-1 text-amber-300" data-testid="pano-audit-missing-count">
        <AlertTriangle className="w-4 h-4" /> {missing.length} required question{missing.length === 1 ? "" : "s"} left
      </span>
    )}
    <span className={sentAt ? "text-sky-300" : "text-slate-500"} data-testid="pano-audit-sent-status">
      {sentAt ? `Sent to Pano ${fmt(sentAt)}${sentBy ? ` by ${sentBy}` : ""}` : "Not sent to Pano yet"}
    </span>
  </div>
);

export default function PanoAuditForm({ project, onSaved }) {
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [meta, setMeta] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [confirmStep, setConfirmStep] = useState(null);

  useEffect(() => {
    // Ignore a stale load that resolves after the crew started typing.
    let live = true;
    Promise.all([api.get("/pano-audit/form"), api.get(`/projects/${project.id}/pano-audit`)])
      .then(([f, a]) => { if (!live) return; setForm(f.data); setAnswers(a.data.answers || {}); setMeta(a.data); })
      .catch(() => { if (live) toast.error("Couldn't load the Pano audit form — check your connection"); });
    return () => { live = false; };
  }, [project.id]);

  const missing = useMemo(() => (form ? missingRequired(form, answers) : []), [form, answers]);
  const skip = useMemo(() => (form ? skippedSections(form, answers) : new Set()), [form, answers]);

  // After a CSV upload, pull the server's Wilson data + auto "Complete" answer without clobbering edits.
  const reloadWilson = async () => {
    const r = await api.get(`/projects/${project.id}/pano-audit`);
    setMeta((m) => ({ ...m, wilson: r.data.wilson }));
    if (r.data.answers?.[WILSON_Q]) setAnswers((cur) => ({ ...cur, [WILSON_Q]: r.data.answers[WILSON_Q] }));
    onSaved?.();
  };

  // Offline-queued CSVs land later — refresh when the background sync replays them.
  useEffect(() => {
    const onQueue = (e) => { if (e.detail?.replayed) reloadWilson().catch(() => {}); };
    window.addEventListener("f2:queue-changed", onQueue);
    return () => window.removeEventListener("f2:queue-changed", onQueue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const setAnswer = (entry, a) => { setAnswers((cur) => ({ ...cur, [entry]: a })); setDirty(true); };

  const save = async () => {
    setBusy("save");
    try {
      const body = { answers };
      const r = await api.post(`/projects/${project.id}/pano-audit`, body, {
        __offlineQueueable: { kind: "post", url: `${API_BASE}/projects/${project.id}/pano-audit`, projectId: project.id, body },
      });
      toast[r.queued ? "info" : "success"](r.queued ? "Saved on this device — will sync when back online" : "Audit saved");
      setDirty(false);
      onSaved?.();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
      return false;
    } finally { setBusy(""); }
  };

  const send = () => {
    if (missing.length) {
      const m = missing[0];
      toast.error(`Answer all required questions first (${missing.length} left). Next: ${m.section} › ${m.q.title.slice(0, 60)}`, { duration: 8000 });
      document.querySelector(`[data-testid="pano-q-${m.q.entry}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const coord = coordCheck(answers, project);
    setConfirmStep(coord && !coord.ok ? { kind: "coord", msg: coord.msg } : { kind: "send" });
  };

  const onConfirm = () => {
    const step = confirmStep;
    setConfirmStep(null);
    if (step?.kind === "coord") setTimeout(() => setConfirmStep({ kind: "send" }), 150);
    else doSend();
  };

  const doSend = async () => {
    const resend = !!meta.sent_to_pano_at;
    if (dirty && !(await save())) return;
    setBusy("send");
    try {
      const r = await api.post(`/projects/${project.id}/pano-audit/send`, { resend });
      setMeta((m) => ({ ...m, sent_to_pano_at: r.data.sent_to_pano_at, sent_by_name: "you" }));
      toast.success("Sent to Pano");
      onSaved?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === "string" && d) || "Couldn't send — check your connection", { duration: 10000 });
    } finally { setBusy(""); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(summaryText(form, answers, project));
      toast.success("Answers copied — paste them into Pano's form");
    } catch { toast.error("Copy failed"); }
  };

  if (!form) return <div className="p-6 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading audit form…</div>;

  return (
    <div className="space-y-4 pb-44 md:pb-24" data-testid="pano-audit-form">
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-4 space-y-2">
          <div className="font-display font-bold text-lg text-slate-50">{form.title.replace(" - Google Form", "")}</div>
          <div className="text-xs text-slate-400">{form.description} Candidate ID and site name are pre-filled from the project — check they match Pano&rsquo;s site list exactly. Enter coordinates from the live MyGPSCoordinates reading at the tower base.</div>
          <StatusBar missing={missing} sentAt={meta.sent_to_pano_at} sentBy={meta.sent_by_name} />
        </CardContent>
      </Card>

      {form.sections.filter((s) => !skip.has(s.id)).map((s) => (
        <Card key={s.id} className="bg-slate-900 border-slate-800" data-testid={`pano-section-${s.id}`}>
          <CardContent className="p-4 space-y-5">
            <div>
              <div className="font-display font-bold uppercase tracking-wider text-sm text-slate-200">{s.title}</div>
              {s.help && <div className="text-[11px] text-slate-400 mt-1">{s.help}</div>}
            </div>
            {s.questions.map((q) => (
              <PanoQuestion key={q.entry} q={q} answer={answers[q.entry]} onChange={(a) => setAnswer(q.entry, a)} />
            ))}
            {s.questions.some((q) => q.entry === LAT_Q) && <CoordWarning answers={answers} project={project} />}
            {s.questions.some((q) => q.entry === WILSON_Q) && (
              <WilsonUploads project={project} wilson={meta.wilson} onUploaded={reloadWilson} />
            )}
          </CardContent>
        </Card>
      ))}

      <div className="fixed bottom-16 md:bottom-0 inset-x-0 md:left-64 z-40 bg-slate-950/95 backdrop-blur border-t border-slate-800 px-4 py-3 flex flex-wrap items-center gap-2">
        <Button onClick={save} disabled={!!busy} className="bg-blue-600 hover:bg-blue-500" data-testid="pano-audit-save">
          {busy === "save" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save{dirty ? " *" : ""}
        </Button>
        <Button onClick={send} disabled={!!busy} variant="outline" className={`border-sky-700 text-sky-200 ${missing.length ? "opacity-60" : ""}`} data-testid="pano-audit-send"
          title={missing.length ? "Answer all required questions first" : ""}>
          {busy === "send" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
          {meta.sent_to_pano_at ? "Resend to Pano" : "Send to Pano"}
        </Button>
        <Button onClick={copy} variant="ghost" className="text-slate-300" data-testid="pano-audit-copy">
          <ClipboardCopy className="w-4 h-4 mr-1" /> Copy answers
        </Button>
        {missing.length > 0 && (
          <span className="text-[11px] text-amber-300/90 truncate max-w-full" data-testid="pano-audit-next-missing">
            Next: {missing[0].section} › {missing[0].q.title.slice(0, 60)}
          </span>
        )}
      </div>
      <SendConfirmDialog step={confirmStep} resend={!!meta.sent_to_pano_at} onCancel={() => setConfirmStep(null)} onConfirm={onConfirm} />
    </div>
  );
}
